import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Search, 
  Mail, 
  Eye,
  Calendar,
  User,
  Building2,
  Bell,
  BellOff,
  Clock,
  Loader2,
  Paperclip,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  AlertTriangle,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const EmailAuditPage = () => {
  const { isBarbiePink } = useTheme();
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [togglingNudge, setTogglingNudge] = useState(null);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const isAdmin = user?.role === 'admin';
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalEmails, setTotalEmails] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fetchOrganizations = async () => {
    try {
      const response = await authFetch(`${API}/organizations?limit=1000`);
      if (response.ok) {
        const result = await response.json();
        setOrganizations(result.data || result);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orgFilter && orgFilter !== 'all') params.append('org_id', orgFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('page', currentPage);
      params.append('limit', pageSize);

      const response = await authFetch(`${API}/sent-emails?${params}`);
      if (response.ok) {
        const result = await response.json();
        setEmails(result.data || []);
        setTotalEmails(result.total || 0);
        setTotalPages(result.total_pages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch emails:', err);
    } finally {
      setLoading(false);
    }
  }, [orgFilter, debouncedSearch, currentPage, pageSize]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [orgFilter, pageSize]);

  const toggleNudge = async (emailId, currentEnabled) => {
    setTogglingNudge(emailId);
    try {
      const response = await authJsonFetch(`${API}/sent-emails/${emailId}/nudge`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !currentEnabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmails(emails.map(e => 
          e.email_id === emailId 
            ? { ...e, nudge_enabled: !currentEnabled, next_nudge_at: data.next_nudge_at }
            : e
        ));
        toast.success(data.message);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to toggle nudge');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setTogglingNudge(null);
    }
  };

  const deleteEmail = async () => {
    if (!emailToDelete) return;
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/sent-emails/${emailToDelete.email_id}`, { method: 'DELETE' });
      
      if (response.ok) {
        toast.success('Email log deleted');
        fetchEmails();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete email');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setDeleting(false);
      setEmailToDelete(null);
    }
  };

  const viewEmail = (email) => {
    setSelectedEmail(email);
    setViewDialogOpen(true);
  };

  const getOrgName = (orgId) => {
    if (!orgId) return '-';
    const org = organizations.find(o => o.org_id === orgId);
    return org?.name || 'Unknown';
  };

  // Use standard date format: DD-MMM-YY HH:MM
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  // Calculate when each nudge is scheduled
  const calculateNudgeSchedules = (email) => {
    if (!email.sent_at) return [];
    const sentDate = new Date(email.sent_at);
    const schedules = [];
    
    if (email.nudge_1_hours > 0) {
      const nudge1Date = new Date(sentDate.getTime() + email.nudge_1_hours * 60 * 60 * 1000);
      schedules.push({
        number: 1,
        date: nudge1Date,
        hours: email.nudge_1_hours,
        sent: email.nudge_count >= 1
      });
    }
    if (email.nudge_2_hours > 0) {
      const nudge2Date = new Date(sentDate.getTime() + email.nudge_2_hours * 60 * 60 * 1000);
      schedules.push({
        number: 2,
        date: nudge2Date,
        hours: email.nudge_2_hours,
        sent: email.nudge_count >= 2
      });
    }
    if (email.nudge_3_hours > 0) {
      const nudge3Date = new Date(sentDate.getTime() + email.nudge_3_hours * 60 * 60 * 1000);
      schedules.push({
        number: 3,
        date: nudge3Date,
        hours: email.nudge_3_hours,
        sent: email.nudge_count >= 3
      });
    }
    
    return schedules;
  };

  const getNudgeLabel = (schedule) => {
    const labels = {
      '48hrs': '48 hours',
      '7days': '7 days',
      '14days': '14 days'
    };
    return labels[schedule] || schedule;
  };

  const emailsWithNudge = emails.filter(e => e.nudge_enabled);

  return (
    <div data-testid="email-audit-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Email Audit</h1>
        <p className="text-muted-foreground mt-1">
          View history of all sent emails and manage nudge reminders ({totalEmails} total)
        </p>
      </div>

      {/* Filters */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="email-search-input"
                placeholder="Search by recipient or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger data-testid="audit-org-filter" className="w-full sm:w-64">
                <SelectValue placeholder="All Organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="email-page-size" className="w-full sm:w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-lg",
                isBarbiePink ? "bg-pink-500/20" : "bg-blue-500/10"
              )}>
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalEmails}</p>
                <p className="text-sm text-muted-foreground">Total Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Mail className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {emails.filter(e => e.status === 'sent').length}
                </p>
                <p className="text-sm text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Bell className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {emailsWithNudge.filter(e => e.nudge_enabled).length}
                </p>
                <p className="text-sm text-muted-foreground">Active Nudges</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Building2 className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(emails.map(e => e.org_id).filter(Boolean)).size}
                </p>
                <p className="text-sm text-muted-foreground">Organizations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="hidden md:table-cell">Organization</TableHead>
                <TableHead className="hidden lg:table-cell">Sent At</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Nudge</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No emails found
                  </TableCell>
                </TableRow>
              ) : (
                emails.map((email) => (
                  <TableRow key={email.email_id} data-testid={`email-row-${email.email_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          isBarbiePink ? "bg-pink-500/20" : "bg-blue-500/10"
                        )}>
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{email.to_name || email.to_email}</p>
                          {email.to_name && (
                            <p className="text-sm text-muted-foreground">{email.to_email}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium line-clamp-1">{email.subject}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {email.org_id ? (
                        <Badge variant="outline">{getOrgName(email.org_id)}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(email.sent_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          email.status === 'sent'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : email.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }
                        title={email.gmail_error || ''}
                        data-testid={`email-audit-status-${email.email_id}`}
                      >
                        {email.status === 'logged' ? 'NOT SENT (logged only)' : email.status}
                      </Badge>
                      {email.gmail_error && (
                        <div className="text-xs text-red-700 dark:text-red-300 mt-1 max-w-xs break-words">
                          {email.gmail_error.length > 80 ? email.gmail_error.slice(0, 80) + '…' : email.gmail_error}
                        </div>
                      )}
                      {email.attachments?.length > 0 && (
                        <Badge variant="outline" className="ml-1">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {email.attachments.length}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {/* Show toggle for any email that has nudge settings (even if disabled) */}
                      {(email.nudge_1_hours > 0 || email.nudge_2_hours > 0 || email.nudge_3_hours > 0 || email.nudge_enabled || email.nudge_count > 0) ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-2">
                                {togglingNudge === email.email_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Switch
                                    checked={email.nudge_enabled}
                                    onCheckedChange={() => toggleNudge(email.email_id, email.nudge_enabled)}
                                    data-testid={`nudge-toggle-${email.email_id}`}
                                  />
                                )}
                                {email.nudge_enabled ? (
                                  <Bell className="w-4 h-4 text-orange-500" />
                                ) : (
                                  <BellOff className="w-4 h-4 text-muted-foreground" />
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {email.nudge_enabled ? (
                                <div className="space-y-2">
                                  <p className="font-medium text-orange-500">Nudges Active - Click to Stop</p>
                                  <div className="text-xs space-y-1">
                                    {calculateNudgeSchedules(email).map((schedule) => (
                                      <div key={schedule.number} className={cn(
                                        "flex items-center gap-2",
                                        schedule.sent ? "text-green-500" : "text-muted-foreground"
                                      )}>
                                        <span>Nudge {schedule.number}:</span>
                                        <span>{formatDate(schedule.date)}</span>
                                        {schedule.sent && <Send className="w-3 h-3" />}
                                      </div>
                                    ))}
                                  </div>
                                  <p className="text-xs">Sent: {email.nudge_count || 0}/{calculateNudgeSchedules(email).length}</p>
                                  {email.next_nudge_at && (
                                    <p className="text-xs font-medium">Next: {formatDate(email.next_nudge_at)}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <p className="font-medium text-muted-foreground">Nudges Stopped</p>
                                  <p className="text-xs">Sent: {email.nudge_count || 0}/{calculateNudgeSchedules(email).length}</p>
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`view-email-${email.email_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => viewEmail(email)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {isAdmin && (
                          <Button
                            data-testid={`delete-email-${email.email_id}`}
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive hover:text-white"
                            onClick={() => setEmailToDelete(email)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination Controls */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalEmails)} of {totalEmails} emails
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-testid="email-first-page-btn"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="email-prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="email-next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-testid="email-last-page-btn"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* View Email Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">To</p>
                  <p className="font-medium">
                    {selectedEmail.to_name && `${selectedEmail.to_name} <`}
                    {selectedEmail.to_email}
                    {selectedEmail.to_name && '>'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sent At</p>
                  <p className="font-medium">{formatDate(selectedEmail.sent_at)}</p>
                </div>
                {selectedEmail.org_id && (
                  <div>
                    <p className="text-muted-foreground">Organization</p>
                    <p className="font-medium">{getOrgName(selectedEmail.org_id)}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge className={
                    selectedEmail.status === 'sent'
                      ? 'bg-green-100 text-green-800'
                      : selectedEmail.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }>
                    {selectedEmail.status === 'logged' ? 'NOT SENT (logged only)' : selectedEmail.status}
                  </Badge>
                  {selectedEmail.gmail_error && (
                    <div className="text-xs text-red-700 dark:text-red-300 mt-2 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
                      <strong>Gmail error:</strong> {selectedEmail.gmail_error}
                    </div>
                  )}
                  {selectedEmail.gmail_message_id && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Gmail message id: <span className="font-mono">{selectedEmail.gmail_message_id}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Nudge Info */}
              {(selectedEmail.nudge_schedule && selectedEmail.nudge_schedule !== 'none') || selectedEmail.nudge_1_hours > 0 ? (
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {selectedEmail.nudge_enabled ? (
                        <Bell className="w-4 h-4 text-orange-500" />
                      ) : (
                        <BellOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="font-medium">
                        Nudge Reminders
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="nudge-toggle">
                        {selectedEmail.nudge_enabled ? 'Enabled' : 'Disabled'}
                      </Label>
                      <Switch
                        id="nudge-toggle"
                        checked={selectedEmail.nudge_enabled}
                        onCheckedChange={() => {
                          toggleNudge(selectedEmail.email_id, selectedEmail.nudge_enabled);
                          setSelectedEmail({
                            ...selectedEmail,
                            nudge_enabled: !selectedEmail.nudge_enabled
                          });
                        }}
                        disabled={togglingNudge === selectedEmail.email_id}
                      />
                    </div>
                  </div>
                  
                  {/* Nudge Schedule Details */}
                  <div className="border rounded-lg divide-y">
                    {calculateNudgeSchedules(selectedEmail).map((schedule) => (
                      <div key={schedule.number} className={cn(
                        "p-3 flex items-center justify-between",
                        schedule.sent ? "bg-green-50" : ""
                      )}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Nudge {schedule.number}</span>
                          <span className="text-sm text-muted-foreground">({schedule.hours}h after original)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{formatDate(schedule.date)}</span>
                          {schedule.sent ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Send className="w-3 h-3 mr-1" />
                              Sent
                            </Badge>
                          ) : selectedEmail.nudge_enabled ? (
                            <Badge className="bg-orange-100 text-orange-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Scheduled
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Paused
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedEmail.next_nudge_at && selectedEmail.nudge_enabled && (
                    <p className="text-sm text-orange-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Next nudge: {formatDate(selectedEmail.next_nudge_at)}
                    </p>
                  )}
                </div>
              ) : null}
              
              <div>
                <p className="text-muted-foreground text-sm mb-1">Subject</p>
                <p className="font-medium text-lg">{selectedEmail.subject}</p>
              </div>
              
              <div>
                <p className="text-muted-foreground text-sm mb-1">Body</p>
                <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap font-mono text-sm">
                  {selectedEmail.body}
                </div>
              </div>

              {/* Attachments */}
              {selectedEmail.attachments?.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2 flex items-center gap-1">
                    <Paperclip className="w-4 h-4" />
                    Attachments ({selectedEmail.attachments.length})
                  </p>
                  <div className="space-y-2">
                    {selectedEmail.attachments.map((att, idx) => (
                      <div key={att.attachment_id || idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{att.original_filename}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(att.file_size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.open(`${API}/sent-emails/${selectedEmail.email_id}/attachments/${idx}/download`, '_blank');
                          }}
                          data-testid={`download-attachment-${idx}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEmail.ai_polished && (
                <Badge variant="secondary">AI Polished</Badge>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Email Confirmation Dialog */}
      <AlertDialog open={!!emailToDelete} onOpenChange={() => setEmailToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Email Log
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this email log?
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <p className="font-medium">{emailToDelete?.subject}</p>
                <p className="text-sm text-muted-foreground">To: {emailToDelete?.to_email}</p>
              </div>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteEmail}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
